import * as R from 'ramda'

import { GetEncodedDispensaryPromosQuery } from '../../../../graphql/camel'
import { CartDispensaryPromoCode } from '../../dispensary-promo'
import { textForAutoShipCodes, textForWellWorldCodes } from './config'
import { findDispensaryPromoTranslatingTo } from './findDispensaryPromo'
import { findRootPromoMatchingCode } from './findRootPromo'
import { PromoCodeUtilities } from './types'
import { sanitizeInput } from './util'

interface ConcealedCode {
  readonly concealed: true
  displayText: string
}

interface VisibleCode {
  readonly concealed: false
  code: string
}

type VisibleOrConcealedCode = VisibleCode | ConcealedCode

interface ConcealCodeFunction {
  (input: VisibleCode): Promise<VisibleOrConcealedCode>
}

const visibleCodeFromInput = (code: string): VisibleCode => ({
  concealed: false,
  code: sanitizeInput(code),
})

const concealWellWorldCodes: ConcealCodeFunction = async (input) => {
  const { code } = input
  const displayText = textForWellWorldCodes[code]
  return displayText ? { concealed: true, displayText } : input
}

const concealAutoShipCodes: ConcealCodeFunction = async (input) => {
  const { code } = input
  const displayText = textForAutoShipCodes[code]
  return displayText ? { concealed: true, displayText } : input
}

/**
 * Conceal any promo code found in `rootPromos` from the `getEncodedDispensaryPromos` query.
 *
 * Conceals code with original code if original code is valid in current dispensary. Otherwise,
 * this function conceals the root promo code with generic discount text.
 */
const concealDispensaryPromoCodes = (
  rootPromos: NonNullable<
    GetEncodedDispensaryPromosQuery['getEncodedDispensaryPromos']
  >['rootPromos'],
  promos: NonNullable<GetEncodedDispensaryPromosQuery['getEncodedDispensaryPromos']>['promos'],
  cartDispensaryPromoCodes: ReadonlyArray<CartDispensaryPromoCode> | undefined,
): ConcealCodeFunction => {
  const findRootPromo = findRootPromoMatchingCode(rootPromos)
  const findOriginalPromo = cartDispensaryPromoCodes
    ? R.pipe(
        findDispensaryPromoTranslatingTo(promos)(cartDispensaryPromoCodes),
        R.andThen((entry) => entry?.dispensaryCode),
      )
    : () => Promise.resolve(undefined)
  return async (input) => {
    const { code } = input
    const rootPromo = await findRootPromo(code)
    return rootPromo
      ? {
          concealed: true,
          displayText: (await findOriginalPromo(code)) || `Dispensary Discount - ${rootPromo.name}`,
        }
      : input
  }
}

/**
 * Conceal any promo code matching an entry in the cart's dispensary promo codes field by
 * replacing with the original (user-entered) code. This function does not validate whether
 * original code is valid in current context.
 */
const concealDispensaryPromoCodesLite =
  (
    cartDispensaryPromoCodes: ReadonlyArray<CartDispensaryPromoCode> | undefined,
  ): ConcealCodeFunction =>
  async (input) => {
    const { code } = input
    const cartDispensaryPromoCode = cartDispensaryPromoCodes?.find(
      ({ rootCode }) => rootCode === code,
    )
    return cartDispensaryPromoCode
      ? {
          concealed: true,
          displayText: cartDispensaryPromoCode.dispensaryCode,
        }
      : input
  }

const ifVisible =
  (f: ConcealCodeFunction) =>
  (input: VisibleOrConcealedCode): Promise<VisibleOrConcealedCode> =>
    input.concealed ? Promise.resolve(input) : f(input)

const resultFromConcealedOrVisibleCode = (result: VisibleOrConcealedCode) =>
  result.concealed ? result.displayText : result.code

export const concealCodeWithoutEncodedPromos = (
  cartDispensaryPromoCodes: CartDispensaryPromoCode[] | undefined,
) =>
  R.pipe(
    visibleCodeFromInput,
    concealWellWorldCodes,
    R.andThen(ifVisible(concealAutoShipCodes)),
    R.andThen(ifVisible(concealDispensaryPromoCodesLite(cartDispensaryPromoCodes))),
    R.andThen(resultFromConcealedOrVisibleCode),
  )

export const concealCodeWithEncodedPromos = (
  rootPromos: NonNullable<
    GetEncodedDispensaryPromosQuery['getEncodedDispensaryPromos']
  >['rootPromos'],
  promos: NonNullable<GetEncodedDispensaryPromosQuery['getEncodedDispensaryPromos']>['promos'],
  cartDispensaryPromoCodes: CartDispensaryPromoCode[] | undefined,
): PromoCodeUtilities['concealCode'] =>
  R.pipe(
    visibleCodeFromInput,
    concealWellWorldCodes,
    R.andThen(ifVisible(concealAutoShipCodes)),
    R.andThen(ifVisible(concealDispensaryPromoCodes(rootPromos, promos, cartDispensaryPromoCodes))),
    R.andThen(resultFromConcealedOrVisibleCode),
  )
